import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";

const AUTOPLAY_MS = 6500;

const GoogleMark = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const Stars = ({ n = 5, size = 12 }) => (
  <span className="inline-flex gap-0.5 text-amber-500">
    {Array.from({ length: n }).map((_, i) => <Star key={i} size={size} fill="currentColor" strokeWidth={0} />)}
  </span>
);

const Rating = ({ data }) => (
  <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link"
    className="flex items-center gap-2.5 shrink-0 hover:opacity-75 transition-opacity">
    <GoogleMark size={18} />
    <span className="font-display font-bold text-ink text-sm">{data.rating.toLocaleString("de-AT", { minimumFractionDigits: 1 })}</span>
    <Stars />
    <span className="text-xs text-faint">Google-Bewertungen</span>
  </a>
);

export const ReviewSlider = ({ data }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const r = data.items[i];

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((n) => (n + 1) % data.items.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, data.items.length]);

  return (
    <section data-testid="reviews-section" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      className="rounded-xl border border-line bg-surface px-5 py-4 md:px-7 md:py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-8">
      <Rating data={data} />
      <span className="hidden md:block w-px self-stretch bg-line" />
      <div className="flex-1 min-w-0 min-h-[3.75rem] flex items-center">
        <AnimatePresence mode="wait">
          <motion.div key={i} data-testid="review-quote" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
            <p className="text-sm text-dim leading-relaxed line-clamp-2">„{r.text}“</p>
            <p className="text-xs text-faint mt-1" data-testid="review-author">{r.name}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex gap-1.5 shrink-0" data-testid="reviews-dots">
        {data.items.map((item, n) => (
          <button key={item.name} type="button" onClick={() => setI(n)} aria-label={`Bewertung ${n + 1}`} data-testid={`reviews-dot-${n}`}
            className={`h-1 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-4 bg-brand" : "w-1 bg-line hover:bg-faint"}`} />
        ))}
      </div>
    </section>
  );
};

export default ReviewSlider;
